/**
 * Input schemas for the videos-and-courses admin — validated once at the
 * server-function boundary, and reused by the admin forms so a field is
 * rejected by the same rule on both sides.
 *
 * Aliases: video admin schema, course admin schema, learning admin inputs
 */

import { z } from "zod";

import { COURSE_STATUSES } from "~/lib/shared/learning/courses";
import {
  isVideoPlacementKey,
  placementAudiences,
  VIDEO_AUDIENCES,
  type VideoPlacementKey,
} from "~/lib/shared/learning/placements";
import {
  assetByKey,
  isAssetKey,
  type AssetKey,
} from "~/lib/shared/nova/assets/catalog";
import { toEmbedUrl } from "~/lib/shared/video-url";

const id = z.string().uuid();
const requiredText = z.string().trim().min(1, "Required");
const optionalText = z.string().trim();

export const videoUrlSchema = z
  .string()
  .trim()
  .refine((url) => toEmbedUrl(url) !== null, {
    message:
      "This link isn't a video we can play. Copy the link from the video's page.",
  });

export const lookupVideoInputSchema = z.object({ url: videoUrlSchema });

const HTTP_LINK_MESSAGE = "Paste the full link, starting with https://";

/** A link is safe to render as an href only when it opens over http(s). */
function isHttpUrl(value: string): boolean {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
}

/** A required web link, rejecting non-http(s) schemes such as javascript: or data:. */
export const webLinkSchema = z
  .string()
  .trim()
  .refine(isHttpUrl, HTTP_LINK_MESSAGE);

export const videoInputSchema = z.object({
  title: requiredText,
  url: videoUrlSchema,
  durationSeconds: z.number().int().positive().nullable(),
  thumbnailUrl: webLinkSchema.nullable(),
});
export type VideoInput = z.infer<typeof videoInputSchema>;

export const updateVideoInputSchema = videoInputSchema.extend({ id });

export const idInputSchema = z.object({ id });

export const setPlacementVideoInputSchema = z
  .object({
    placement: z.custom<VideoPlacementKey>(
      (value) => typeof value === "string" && isVideoPlacementKey(value),
      { message: "Unknown placement" },
    ),
    audience: z.enum(VIDEO_AUDIENCES),
    /** Null clears the audience, so it falls back to the next one. */
    videoId: id.nullable(),
  })
  .refine(
    (input) => placementAudiences(input.placement).includes(input.audience),
    {
      message: "That placement has no video for this audience",
      path: ["audience"],
    },
  );
export type SetPlacementVideoInput = z.infer<
  typeof setPlacementVideoInputSchema
>;

export const courseSlugSchema = z
  .string()
  .trim()
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Lowercase letters, numbers and single hyphens only.",
  );

export const courseInputSchema = z.object({
  slug: courseSlugSchema,
  title: requiredText,
  headline: requiredText,
  description: optionalText,
});
export type CourseInput = z.infer<typeof courseInputSchema>;

export const updateCourseDetailsInputSchema = courseInputSchema.extend({ id });

export const setCourseStatusInputSchema = z.object({
  id,
  status: z.enum(COURSE_STATUSES),
});

const linkMaterial = {
  title: requiredText,
  note: optionalText,
  url: webLinkSchema,
};

export const courseMaterialInputSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("slides"), ...linkMaterial }),
  z.object({ kind: z.literal("template"), ...linkMaterial }),
  z.object({
    kind: z.literal("replay"),
    title: requiredText,
    note: optionalText,
    videoId: id,
  }),
]);
export type CourseMaterialInput = z.infer<typeof courseMaterialInputSchema>;

export const courseWeekInputSchema = z.object({
  title: requiredText,
  summary: optionalText,
  outcome: optionalText,
  classVideoId: id.nullable(),
  /**
   * The NOVA assets the week builds. Approving all of them completes it. Only
   * `surface: "assets"` catalog entries qualify: a `surface: "content"` entry
   * (articles, LinkedIn posts, client stories) keeps no approval history, so a
   * week tied to one could come undone later.
   */
  producesAssets: z
    .array(
      z.custom<AssetKey>(
        (value) =>
          typeof value === "string" &&
          isAssetKey(value) &&
          assetByKey(value).surface === "assets",
        { message: "Weeks build NOVA assets, not content." },
      ),
    )
    .refine((keys) => new Set(keys).size === keys.length, {
      message: "Each asset can only be chosen once.",
    }),
  /** The week's whole material list, in order. Saving replaces it. */
  materials: z.array(courseMaterialInputSchema),
});
export type CourseWeekInput = z.infer<typeof courseWeekInputSchema>;

export const createCourseWeekInputSchema = z.object({
  courseId: id,
  title: requiredText,
});

export const updateCourseWeekInputSchema = courseWeekInputSchema.extend({
  id,
});

export const reorderCourseWeeksInputSchema = z.object({
  courseId: id,
  /** Every week of the course, in the new order. */
  weekIds: z.array(id).min(1),
});
